<script setup lang="ts">
import CoreCalendar from 'frontcore/presentation/calendar/views/CoreCalendar.vue'
import CoreContextMenu from 'frontcore/views/context-menu/CoreContextMenu.vue'
import i18n from 'frontcore/i18n'
import InputHelperText from "frontcore/views/select/InputHelperText.vue"
import SVGIcon from 'frontcore/views/icons/SVGIcon.vue'
import useInputDate from 'frontcore/presentation/calendar/domain/useInputDate'
import useSelectState from 'frontcore/views/select/useSelectState'
import { CalendarModeOptions, DisabledDateFunction, InputDateViewModel } from 'frontcore/presentation/calendar/domain/types'
import { computed, reactive, ref, toRefs } from 'vue'
import { ContextMenuPosition } from 'frontcore/views/context-menu/useContextMenuPosition'
import { Nullable } from 'frontcore/presentation/types'

interface Props {
  dataTest?: string
  disableFutureDays?: boolean
  disablePastDays?: boolean
  disabled?: boolean
  disabledDays?: Nullable<DisabledDateFunction | Date[]>
  error?: string
  label?: string
  nullable?: boolean
  placeholder?: string
  range?: boolean
  selectionMode?: CalendarModeOptions
  supportingText?: string
  value?: InputDateViewModel['selectedDate']
}
const props = withDefaults(defineProps<Props>(), {
  dataTest: 'core-date-picker',
  disableFutureDays: false,
  disablePastDays: false,
  disabledDays: () => [],
  disabled: false,
  label: '',
  nullable: true,
  range: false,
  selectionMode: CalendarModeOptions.SELECT_DAY,
  value: null,
})

const emit = defineEmits<(e: 'input', selectedValue: InputDateViewModel['selectedDate']) => void>()

const {
  disableFutureDays,
  disablePastDays,
  disabled,
  disabledDays,
  error,
  nullable,
  range,
  selectionMode,
  value,
} = toRefs(props)

const { formattedDateValue } = useInputDate({ value, range, nullable, selectionMode }, emit)

const {
  chevronStyle,
  clickOutside,
  isFocused,
  showClearButton,
  stateStyleClass,
  toggleFocus,
} = useSelectState(reactive({ disabled, error, value: formattedDateValue, nullable }))

const { t } = i18n.global

const isShowingPlaceholder = computed<boolean>(() => formattedDateValue.value === '')

const selectedDateLabel = computed<string>(() => {
  if (isShowingPlaceholder.value) {
    return props.placeholder ?? t('common.messages.date')
  }
  return formattedDateValue.value
})

const rootElement = ref<HTMLDivElement | null>(null)
const selectValue = (selected: InputDateViewModel['selectedDate']) => {
  emit('input', selected)
  toggleFocus()
}

const clearSelectedDate = () => {
  const defaultValue: InputDateViewModel['selectedDate'] = range.value ? [] : null
  emit('input', defaultValue)
  emitNativeClick()
}

const emitNativeClick = () => {
  rootElement.value?.click()
}

const contextMenuAnchor = ref<HTMLElement | null>(null)
const contextMenuPositions: ContextMenuPosition[] = [
  ContextMenuPosition.bottomRight, ContextMenuPosition.bottomLeft,
  ContextMenuPosition.topRight, ContextMenuPosition.topLeft,
]
</script>

<template>
  <div
    ref="rootElement"
    v-click-outside="clickOutside"
    class="input-text__container core-calendar"
    :class="[stateStyleClass, {'pointer': !disabled, 'input-text__state-disabled': disabled}]"
    :data-test="dataTest">
    <span 
      v-if="label"
      class="input-text__label tw-inline-block">
      {{ label }}
    </span>
    <div
      ref="contextMenuAnchor"
      class="input-text__input-container"
      @click="toggleFocus">
      <div class="input-text__icon-container">
        <SVGIcon
          class="input-text__left-icon"
          iconName="CalendarMonth"
          :remSize="1.25" />
      </div>
      <span
        class="input-text__input"
        :class="{'input-text__input--placeholder': isShowingPlaceholder}"
        data-test="core-calendar-input">{{ selectedDateLabel }}</span>
      <div
        v-show="showClearButton"
        class="input-text__icon-container input-text__icon-container--small">
        <SVGIcon
          data-test="clear-button"
          iconName="Close"
          :colorClass="disabled ? 'tw-fill-core-neutral-cold-50' : 'tw-fill-core-neutral-cold-40'"
          :remSize="1"
          @click.stop="clearSelectedDate" />
      </div>
      <div
        class="input-text__icon-container"
        data-test="icon__arrow-dropdown">
        <SVGIcon
          class="input-text__chevron"
          iconName="ArrowDropDown"
          :colorClass="disabled ? 'tw-fill-core-neutral-cold-50' : 'tw-fill-core-neutral-cold-20'"
          :remSize="1.25"
          :style="chevronStyle" />
      </div>
    </div>
    <CoreContextMenu
      v-if="isFocused"
      :anchor="contextMenuAnchor"
      :positions="contextMenuPositions">
      <CoreCalendar
        :disableFutureDays="disableFutureDays"
        :disablePastDays="disablePastDays"
        :disabled="disabled"
        :disabledDays="disabledDays"
        :range="range"
        :selectionMode="selectionMode"
        :value="value"
        @input="selectValue" />
    </CoreContextMenu>
    <InputHelperText
      :class="{invisible: isFocused}"
      :error="error"
      :supportingText="supportingText" />
  </div>
</template>
